import { Hub } from 'aws-amplify/utils'

// How long a session lasts after the user signs in: one working day. Amplify
// renews the token until then, and then the user has to sign in again. Product
// decided this in the weekly software meeting of 23 October 2026.
export const SESSION_MAX_AGE = 8 * 60 * 60 * 1000

// Hub channel and event that report the end of the session. The listener in
// sessionListener.js turns them into one sessionExpired.
export const SESSION_CHANNEL = 'session'
export const SESSION_LIMIT_REACHED = 'limitReached'
export const SESSION_REJECTED = 'rejected'

/**
 * Tells when the session has to end.
 *
 * The time comes from auth_time, the moment the user signed in. Cognito keeps
 * it when it renews the token, so the limit counts from the sign in and not
 * from the last renewal.
 *
 * @param {Object} [tokens] - Tokens from fetchAuthSession.
 * @returns {number|undefined} The deadline in ms since epoch, or undefined
 *   when there is no session.
 */
export function getSessionDeadline(tokens) {
  const authTime = tokens?.accessToken?.payload?.auth_time
  if (typeof authTime !== 'number') return undefined

  return authTime * 1000 + SESSION_MAX_AGE
}

/**
 * Tells if a deadline has passed.
 *
 * @param {number} [deadline] - A deadline from getSessionDeadline.
 * @param {number} [now] - The current time in ms since epoch.
 * @returns {boolean} True once the deadline has passed. False with no deadline.
 */
export function isPastDeadline(deadline, now = Date.now()) {
  return deadline !== undefined && now >= deadline
}

/**
 * Tells if the session has passed its deadline.
 *
 * @param {Object} [tokens] - Tokens from fetchAuthSession.
 * @param {number} [now] - The current time in ms since epoch.
 * @returns {boolean} True once the deadline has passed. False with no session.
 */
export function isSessionOverLimit(tokens, now = Date.now()) {
  return isPastDeadline(getSessionDeadline(tokens), now)
}

// Name of the error a request fails with when the session is over the limit.
export const SESSION_LIMIT_ERROR = 'SessionLimitError'

/**
 * Builds the error a request fails with when the session is over the limit.
 *
 * The listener in sessionListener.js already signs the user out and shows the
 * message, so code that catches this error must not do it again.
 *
 * @returns {Error} An error with the name SESSION_LIMIT_ERROR.
 */
export function createSessionLimitError() {
  const error = new Error('Session limit reached')
  error.name = SESSION_LIMIT_ERROR
  return error
}

/**
 * Tells if an error comes from a session over the limit.
 *
 * @param {Error} [error] - Any error.
 * @returns {boolean} True for the error from createSessionLimitError.
 */
export const isSessionLimitError = error => error?.name === SESSION_LIMIT_ERROR

/**
 * Reports that the API rejected the session with a 401 or 403, or that the
 * page load could not load the user.
 *
 * The listener in sessionListener.js ends the session once, so the caller
 * must not show its own message or open the login page.
 */
export function reportSessionRejected() {
  Hub.dispatch(SESSION_CHANNEL, { event: SESSION_REJECTED })
}

/**
 * Reports that the session reached its deadline.
 *
 * Any code that finds the session over the limit calls this. The listener in
 * sessionListener.js ends the session once, however many reports come in.
 */
export function reportSessionLimitReached() {
  Hub.dispatch(SESSION_CHANNEL, { event: SESSION_LIMIT_REACHED })
}
